import {
  ViewFilled16,
  ViewOffFilled16,
  WarningAltFilled20,
} from '@carbon/icons-react';
import { OktaContext } from '@okta/okta-react';
import {
  useLocationPathname,
  useLocationSearch,
  useNavigate,
} from '@rocketlane/rocket-router';
// eslint-disable-next-line no-restricted-imports
import { Col, Row } from 'antd';
import { useContext, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { AccountAPI, AccountMeta } from 'api/account';
import { Checkbox } from 'components/atoms/checkbox';
import { Form } from 'components/atoms/form';
import {
  BackgroundColor,
  Input,
  InputBorder,
  InputFocus,
} from 'components/atoms/input/input';
import { Color } from 'components/colors';
import { Button } from 'components/facade/button';
import { BodyShort01, Text01 } from 'components/styles';
import { useTranslate } from 'hooks/use-translation';
import { useIsSamlMandatory } from '../saml';
import { ForgotPassword } from './forgot-password';
import { SsoSamlButton } from './sso-saml-button';
import { useCognitoAuth } from './use-cognito-auth';

enum PasswordInputType {
  PASSWORD = 'password',
  TEXT = 'text',
}

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 400px;
  & button {
    height: 50px;
  }
  margin-bottom: var(--spacing-05);
`;

const WidgetContainer = styled.div`
  width: 100%;
  max-width: 400px;
  display: flex;
  flex-direction: column;
  position: relative;

  .ant-form-item {
    margin-bottom: 0 !important;
  }

  .ant-form-item:nth-child(1) {
    padding-bottom: var(--spacing-03) !important;
  }

  .ant-form-item-control-input-content input {
    width: 100%;
    padding: 0 var(--spacing-05);
    ${InputBorder};
    ${BodyShort01};
    ${Text01};
    ${BackgroundColor};
    height: var(--input-height-lg);

    &.focused-input {
      ${InputFocus};
    }
  }
`;

const SignInButton = styled(Button)`
  text-align: left;
  border-radius: 0;
  border-color: var(--blue-60);
  background: var(--blue-60);
  color: var(--white-100);
  height: var(--input-height-lg);
  margin-bottom: var(--spacing-03);
`;

const Extras = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--spacing-02);
  margin-bottom: var(--spacing-07);
`;

const StyledCheckbox = styled(Checkbox)`
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px !important;
  font-weight: 400 !important;
  font-family: var(--font-family) !important;
`;

const AlertWrapper = styled.div<{ color: Color }>`
  display: flex;
  align-items: center;
  justify-content: start;
  padding: var(--spacing-04);
  margin: var(--spacing-05) 0;
  background-color: var(--red-10);
  border-left: 4px solid var(--red-50);
  color: var(--red-60);

  svg {
    margin-right: var(--spacing-03);
    fill: var(--red-50);
  }
`;

const PasswordContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const ShowPasswordContainer = styled.div`
  cursor: pointer;
  position: absolute;
  right: var(--spacing-05);
  color: var(--gray-30);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const errorMessages = [
  'UserMigration failed with error User account already exists.',
  'UserMigration failed with error User migrated successfully.',
];

const lambdaErrorMessages = [
  'UserMigration failed with error Incorrect username or password.',
];

export const CustomSignInWidget = ({ data }: { data: AccountMeta }) => {
  const translate = useTranslate();
  const [form] = Form.useForm();
  const pathname = useLocationPathname();
  const search = useLocationSearch();
  const navigate = useNavigate();
  const isSamlMandate = useIsSamlMandatory();
  const [checked, setChecked] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [notAnErrorBuErrorMessage, setNotAnErrorBuErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [passwordInputType, setPasswordInputType] = useState(
    PasswordInputType.PASSWORD,
  );

  const isCustomAuthentication = data?.okta?.customAuthentication;

  const [showPassword, setShowPassword] = useState(false);
  const isSamlEnabled = useMemo(
    () => !!(data?.okta.idps ?? []).filter((idp) => idp.enabled).length,
    [data?.okta.idps],
  );

  const onClick = () => {
    setErrorMessage('');
    form.validateFields().then((values) => {
      const { email, password } = values.information[0];

      if (!email || !password) {
        setLoading(false);

        return;
      }

      setLoading(true);

      AccountAPI.loginWithCognito(
        {
          userName: email,
          password,
          clientId: data?.okta?.clientId,
          userNamesuffix: data?.okta?.userName,
          issuerDomain: data?.okta?.issuerDomain,
        },
        isCustomAuthentication,
      )
        // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
        .then((res: { token?: string }) => {
          setLoading(false);
          const { token } = res;

          navigate(`${pathname}?token=${token}&cognito=${true}`);
        })
        .catch((error) => {
          if (errorMessages.includes(error?.message)) {
            setNotAnErrorBuErrorMessage(
              translate('components.login.something_went_wrong_try_again'),
            );
          } else if (lambdaErrorMessages.includes(error?.message)) {
            setErrorMessage(
              translate('components.login.incorrect_username_or_password'),
            );
          } else if (error?.response?.data?.errors[0]?.errorMessage) {
            setErrorMessage(error.response.data.errors[0].errorMessage);
          } else if (error?.message) {
            setErrorMessage(error.message);
          }
          setLoading(false);
        });
    });
  };

  const { samlFlowHandler, googleSSOHandler } = useCognitoAuth({ data });
  const oktaAuth = useContext(OktaContext)?.oktaAuth;
  const params = new URLSearchParams(search);
  const partnerParam = params.get('partner');

  return (
    <WidgetContainer>
      {isSamlMandate && isSamlEnabled && !partnerParam ? (
        <ButtonContainer>
          {(data.okta.idps ?? [])
            .filter((idp) => idp.enabled)
            .map((idp) => {
              return (
                <SsoSamlButton
                  key={idp?.idpId}
                  idp={idp}
                  googleSSOHandler={googleSSOHandler}
                  samlFlowHandler={samlFlowHandler}
                  isCognitoEnabled
                  idpId={idp.idpId}
                  oktaAuth={oktaAuth}
                />
              );
            })}
        </ButtonContainer>
      ) : (
        <>
          {errorMessage && (
            <AlertWrapper color="red">
              <WarningAltFilled20 />
              {errorMessage}
            </AlertWrapper>
          )}

          {notAnErrorBuErrorMessage && (
            <AlertWrapper color="blue">
              <WarningAltFilled20 />
              {notAnErrorBuErrorMessage}
            </AlertWrapper>
          )}
          <Form
            form={form}
            layout="vertical"
            initialValues={{ information: [{ email: '', password: '' }] }}
            onKeyDownCapture={(e) => {
              if (e.key === 'Enter' && !loading) {
                form.validateFields().then(() => {
                  const { email, password } =
                    form.getFieldValue('information')[0];

                  if (email && password) {
                    e.preventDefault();
                    onClick();
                  }
                });
              }
            }}
          >
            <Form.List name="information">
              {(fields) => {
                return (
                  <>
                    {fields.map((field) => (
                      <Row key={field?.key}>
                        <Col span={24}>
                          {/* eslint-disable-next-line react/jsx-props-no-spreading */}
                          <Form.Item
                            id="email"
                            {...field}
                            name={[field.name, 'email']}
                            fieldKey={[field.fieldKey ?? '', 'email']}
                            label={<FormattedMessage id="email" />}
                            rules={[
                              {
                                required: true,
                                message: (
                                  <FormattedMessage id="please_enter_email" />
                                ),
                              },
                            ]}
                          >
                            <Input
                              placeholder={translate('email')}
                              type="email"
                              name="email"
                              autoComplete="on"
                            />
                          </Form.Item>
                        </Col>
                        <Col span={24}>
                          {/* eslint-disable-next-line react/jsx-props-no-spreading */}
                          <Form.Item
                            id="password"
                            {...field}
                            name={[field.name, 'password']}
                            fieldKey={[field.fieldKey ?? '', 'password']}
                            label={<FormattedMessage id="password" />}
                            rules={[
                              {
                                required: true,
                                message: (
                                  <FormattedMessage id="please_enter_password" />
                                ),
                              },
                            ]}
                          >
                            <PasswordContainer>
                              <Input
                                placeholder={translate('password')}
                                type={passwordInputType}
                                name="password"
                              />
                              <ShowPasswordContainer
                                onClick={() => {
                                  setShowPassword(!showPassword);
                                  setPasswordInputType(
                                    showPassword
                                      ? PasswordInputType.PASSWORD
                                      : PasswordInputType.TEXT,
                                  );
                                }}
                              >
                                {!showPassword ? (
                                  <ViewFilled16 />
                                ) : (
                                  <ViewOffFilled16 />
                                )}
                              </ShowPasswordContainer>
                            </PasswordContainer>
                          </Form.Item>
                        </Col>
                      </Row>
                    ))}
                  </>
                );
              }}
            </Form.List>
          </Form>
          <Extras>
            <StyledCheckbox
              checked={checked}
              onChange={(event) => {
                setChecked(event.target.checked);
              }}
            >
              <FormattedMessage id="remember_me" />
            </StyledCheckbox>
            <ForgotPassword isSamlMandate={isSamlMandate} />
          </Extras>
          <SignInButton loading={loading} onClick={onClick}>
            <FormattedMessage id="magic_link.otp.sigin.button.text" />
          </SignInButton>
        </>
      )}
    </WidgetContainer>
  );
};
